/**
 * The board — the performance surface, and the screen every other one is judged against.
 *
 * Rules it lives by:
 *
 * - **Audio before anything.** `onPadDown` calls `triggerPad` and nothing else runs first.
 *   The visual state is written afterwards, on the frame scheduler.
 * - **The surface never scrolls.** `lockInstrumentSurface` and `touch-action: none` hand
 *   every gesture to the grid, so a fast run of hits cannot rubber-band the page or fire
 *   pull-to-refresh in the middle of a take.
 * - **Two fingers change bank, one finger plays.** A single-finger swipe would have to
 *   cancel a trigger that has already sounded, so bank changes are a two-finger gesture on
 *   the grid and a one-finger swipe on the bank rail, and neither steals a hit.
 * - **Every gesture has a non-gesture equivalent.** Long-press opens the pad menu; so does a
 *   plain tap once "Edit pads" is on, which is the route VoiceOver and a keyboard take. That
 *   mode change is announced, and every tile says it is in it, because a mode that silently
 *   redefines twelve controls is worse than no mode at all.
 */

/** Appended to every tile's spoken description while the board is in edit mode. */
const EDIT_MODE_NOTE = 'Edit mode: opens this pad’s options instead of playing it.';

import { h, on, onCleanup } from '../../core/dom';
import { computed, effect } from '../../core/signal';
import { icon, iconButton, neonButton, segmentedRail } from '../components/controls';
import { padTile, type PadTileHandle } from '../components/pad';
import { stereoMeter } from '../components/meters';
import { confirmDialog } from '../components/sheet';
import { lockInstrumentSurface, layout } from '../../platform/viewport';
import { holdScreenAwake } from '../../platform/wake-lock';
import {
  GRID_SIZES,
  PAD_COLORS,
  PLAYBACK_MODES,
  QUANTIZE_OPTIONS,
  RETRIGGER_BEHAVIOURS,
  adaptiveGrid,
  gridColumns,
  gridDisplayName,
  gridRows,
  makePad,
  padColorDisplayName,
  padColorVariable,
  padIsEmpty,
  playbackModeDisplayName,
  playbackModeSummary,
  quantizeDisplayName,
  resizeBank,
  retriggerDisplayName,
  retriggerSummary,
  type Pad,
  type Quantize,
} from '../../model/types';
import {
  assignAssetToPad,
  boardEditMode,
  currentBank,
  currentBankIndex,
  engine,
  mutate,
  notify,
  notifyUndoable,
  padClipboard,
  padStateFor,
  panic,
  clearPadArtwork,
  padArtworkURL,
  reportFailure,
  setPadArtwork,
  project,
  releasePad,
  revision,
  selectedPadID,
  settings,
  stopAllForEditing,
  triggerPad,
  togglePad,
} from '../../app/state';
import { navigate } from '../../app/router';
import { claimEditorSurface } from '../../app/transitions';
import { importDroppedFiles, pickFile, shareProjectBackup } from '../../app/import';
import { historyButtons, presentMenu, presentPrompt, requiresProject, screenHeader } from '../../app/shell';

/**
 * Puts a file straight onto the pad the user was holding.
 *
 * Importing used to mean going to the Library, importing there, then finding the sound and
 * assigning it — three screens away from the pad they were already touching. Worse, the only
 * other Import button in the app is Home's, which reads `.padlab` backups, so someone with an
 * MP3 reached for it and was told their music was not a valid backup.
 *
 * The input is built per invocation and thrown away. A long-lived hidden input on the board
 * is a control in the accessibility tree that nothing points at, and holding a `File`
 * reference alive after the import keeps the whole decoded blob in memory.
 */
async function importIntoPad(pad: Pad): Promise<void> {
  const file = await pickAudioFile();
  if (file === null) return;

  const outcome = await importDroppedFiles([file], 'picker');
  const asset = outcome.imported[0]?.asset;
  // `importDroppedFiles` has already told the user why nothing arrived — a second banner
  // saying the same thing in different words is noise, not clarity.
  if (asset === undefined) return;

  assignAssetToPad(pad.id, asset.id);
  notify('Added to the pad', asset.name);
}

/**
 * What the pickers will accept.
 *
 * The bare MIME wildcards are not enough on iOS. The Files picker matches on UTI, its mapping
 * from `audio/*` is incomplete, and the practical result is that ordinary files are shown
 * greyed out and cannot be chosen — an mp3 sitting in Files being the case that was reported.
 * Listing extensions alongside the wildcard is what makes them selectable, and it costs
 * nothing on platforms that already understood the wildcard.
 */
const AUDIO_ACCEPT =
  'audio/*,video/*,.mp3,.m4a,.aac,.wav,.aif,.aiff,.caf,.flac,.ogg,.oga,.opus,.mp4,.mov,.m4v,.3gp';
const IMAGE_ACCEPT = 'image/*,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.bmp,.tif,.tiff';

/**
 * Resolves with the chosen file, or null if the picker was dismissed.
 *
 * Delegates to the one picker in the app rather than being a second implementation of it.
 * There used to be three: this one, `pickFile`, and a third written inline for pad artwork
 * that forgot to attach its input to the document and never fired on iOS. Two of the three
 * were correct, which is exactly why nobody noticed the third.
 */
function pickAudioFile(): Promise<File | null> {
  return pickFile(AUDIO_ACCEPT);
}


/**
 * Removes the current bank, its pads, and anything that pointed at them.
 *
 * Banks could only ever be created. Slicing a loop into pads makes one automatically, named
 * after the sample, so they accumulated with no way to get rid of them.
 *
 * Deleting a bank is not just dropping it from the array. Sequencer tracks are bound by
 * `padID`, so every track whose pad lived in this bank has to go too, or the pattern keeps
 * rows that can never sound and the grid fills with silent lanes. Automation lanes targeted at
 * those pads go with them for the same reason.
 *
 * The whole thing runs inside one `mutate`, so it is a single undo step: the board, its tracks
 * and its automation come back together or not at all.
 */
function confirmDeleteBank(): void {
  const open = project.peek();
  if (open === null || open.banks.length <= 1) return;
  const index = currentBankIndex.peek();
  const bank = open.banks[index];
  if (bank === undefined) return;

  const padIDs = new Set(bank.pads.map((pad) => pad.id));
  const loaded = bank.pads.filter((pad) => !padIsEmpty(pad)).length;
  const tracks = open.patterns.reduce(
    (total, pattern) => total + pattern.tracks.filter((track) => track.padID !== undefined && padIDs.has(track.padID)).length,
    0,
  );

  const parts = [`${loaded} pad${loaded === 1 ? '' : 's'} with a sound`];
  if (tracks > 0) parts.push(`${tracks} sequencer track${tracks === 1 ? '' : 's'}`);

  confirmDialog({
    title: `Delete “${bank.name}”?`,
    message:
      loaded === 0 && tracks === 0
        ? 'This bank is empty. Your sounds stay in the library either way.'
        : `This removes ${parts.join(' and ')}. The sounds themselves stay in your library, and this can be undone.`,
    confirmTitle: 'Delete bank',
    destructive: true,
    onConfirm: () => {
      mutate('Delete Bank', (target) => {
        const live = target.banks[index];
        if (live === undefined || target.banks.length <= 1) return;
        const ids = new Set(live.pads.map((pad) => pad.id));
        target.banks.splice(index, 1);
        for (const pattern of target.patterns) {
          pattern.tracks = pattern.tracks.filter((track) => track.padID === undefined || !ids.has(track.padID));
          pattern.automation = pattern.automation.filter((lane) => {
            const targetLane = lane.target;
            if (typeof targetLane !== 'object' || targetLane === null) return true;
            const padID = (targetLane as { padID?: string }).padID;
            return padID === undefined || !ids.has(padID);
          });
        }
      });
      // Clamp after the array shrank, or the board points past its own last bank.
      const remaining = project.peek()?.banks.length ?? 1;
      currentBankIndex.value = Math.min(index, remaining - 1);
      selectedPadID.value = null;
      notify('Bank deleted', 'Undo is in the toolbar.');
    },
  }).present();
}

function padMenu(pad: Pad): void {
  const open = project.peek();
  if (open === null) return;
  // The menu covers the board, so anything still sounding is a loop whose tile the user can no
  // longer reach, and every choice in here is made about a sound they cannot hear properly.
  stopAllForEditing();
  const empty = padIsEmpty(pad);
  presentMenu(pad.name === '' ? `Pad ${pad.slot + 1}` : pad.name, [
    {
      title: empty ? 'Assign a sound' : 'Replace the sound',
      symbol: 'waveform.path',
      onSelect: () => {
        selectedPadID.value = pad.id;
        void navigate({ name: 'library', projectID: open.id });
      },
    },
    {
      title: 'Edit sample',
      symbol: 'slider.horizontal.3',
      disabled: empty,
      onSelect: () => {
        selectedPadID.value = pad.id;
        // The tile the user tapped becomes the editor's waveform, rather than the whole
        // board cross-fading into a screen with no relationship to what was touched.
        claimEditorSurface(document.querySelector<HTMLElement>(`.pad[data-pad-id="${pad.id}"]`));
        void navigate({ name: 'wave', projectID: open.id, ...(pad.assetID === undefined ? {} : { assetID: pad.assetID }) });
      },
    },
    {
      // Short, and no detail line. Every other row here is a bare verb phrase, and the long
      // explanation this used to carry wrapped over its own title and clipped at the row edge.
      title: 'Import a sound',
      symbol: 'square.and.arrow.down',
      onSelect: () => void importIntoPad(pad),
    },
    {
      title: 'Record into this pad',
      symbol: 'record.circle.fill',
      onSelect: () => {
        selectedPadID.value = pad.id;
        void navigate({ name: 'voice-lab', projectID: open.id });
      },
    },
    {
      title: 'Rename',
      symbol: 'mouth.fill',
      onSelect: () =>
        presentPrompt({
          title: 'Pad name',
          value: pad.name,
          onConfirm: (name) => mutate('Rename Pad', (target) => {
            const live = target.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id);
            if (live !== undefined) live.name = name;
          }),
        }),
    },
    { title: 'Copy', symbol: 'square.grid.3x3.fill', disabled: empty, onSelect: () => {
      padClipboard.value = structuredClone(pad);
      notify('Pad copied');
    } },
    {
      title: 'Paste',
      symbol: 'square.and.arrow.up',
      disabled: padClipboard.peek() === null,
      onSelect: () => {
        const source = padClipboard.peek();
        if (source === null) return;
        mutate('Paste Pad', (target) => {
          const live = target.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id);
          if (live === undefined) return;
          const slot = live.slot;
          const id = live.id;
          Object.assign(live, structuredClone(source), { id, slot });
        });
      },
    },
    {
      title: 'Duplicate to the next free pad',
      symbol: 'square.grid.3x3.fill',
      disabled: empty,
      onSelect: () =>
        mutate('Duplicate Pad', (target) => {
          const bank = target.banks[currentBankIndex.peek()];
          if (bank === undefined) return;
          const free = bank.pads.find((entry) => padIsEmpty(entry) && entry.slot > pad.slot) ?? bank.pads.find((entry) => padIsEmpty(entry));
          if (free === undefined) return;
          const id = free.id;
          const slot = free.slot;
          Object.assign(free, structuredClone(pad), { id, slot });
        }),
    },
    { title: 'Colour…', symbol: 'circle.circle.fill', onSelect: () => colourMenu(pad) },
    { title: 'Icon…', symbol: 'star.fill', detail: pad.symbolName ?? 'None', onSelect: () => symbolMenu(pad) },
    { title: 'Category…', symbol: 'folder.fill', detail: pad.category ?? 'None', onSelect: () => categoryMenu(pad) },
    {
      title: 'Tags…',
      symbol: 'music.note.list',
      detail: pad.tags.length === 0 ? 'None' : pad.tags.join(', '),
      onSelect: () =>
        presentPrompt({
          title: 'Tags, separated by commas',
          value: pad.tags.join(', '),
          onConfirm: (text) =>
            editPad(pad.id, 'Pad Tags', (live) => {
              live.tags = text
                .split(',')
                .map((entry) => entry.trim())
                .filter((entry) => entry !== '');
            }),
        }),
    },
    { title: 'Artwork…', symbol: 'square.grid.3x3.fill', onSelect: () => artworkMenu(pad) },
    { title: 'Playback mode…', symbol: 'play.fill', detail: playbackModeDisplayName(pad.playbackMode), onSelect: () => modeMenu(pad) },
    { title: 'Retrigger…', symbol: 'arrow.counterclockwise', detail: retriggerDisplayName(pad.retrigger), onSelect: () => retriggerMenu(pad) },
    { title: 'Quantise…', symbol: 'clock.fill', detail: quantizeDisplayName(pad.quantize), onSelect: () => quantiseMenu(pad) },
    { title: 'Choke group…', symbol: 'door.left.hand.closed', detail: pad.chokeGroup === undefined ? 'None' : `Group ${pad.chokeGroup}`, onSelect: () => chokeMenu(pad) },
    {
      title: 'Clear pad',
      symbol: 'trash.fill',
      destructive: true,
      disabled: empty,
      onSelect: () => {
        confirmDialog({
          title: 'Clear this pad?',
          message: 'The sound stays in your library. Only this pad is emptied, and undo brings it back.',
          confirmTitle: 'Clear',
          destructive: true,
          onConfirm: () => {
            mutate('Clear Pad', (target) => {
              // The whole pad, not just its sound. Deleting `assetID` and blanking the name
              // left the mute, the solo, the fader, the pan, the choke group and the entire
              // edit behind, so the next sound dropped here played through the last one's
              // chain — or silently, because the pad was still muted. `makePad` is the
              // model's own definition of an empty pad; the id and the slot are what has to
              // survive, because the board addresses pads by id.
              for (const bank of target.banks) {
                const index = bank.pads.findIndex((entry) => entry.id === pad.id);
                if (index < 0) continue;
                const live = bank.pads[index]!;
                bank.pads[index] = makePad(live.slot, { id: live.id });
                break;
              }
            });
            notifyUndoable('Pad cleared', 'The sound stays in your library.');
          },
        }).present();
      },
    },
  ]);
}

function editPad(padID: string, label: string, change: (pad: Pad) => void): void {
  mutate(label, (target) => {
    const live = target.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === padID);
    if (live !== undefined) change(live);
  });
}

/**
 * The icon vocabulary offered on the web.
 *
 * `Pad.symbolName` stores SF Symbol names so a pad chosen on the phone renders here and vice
 * versa, and the icon layer already owns the name → SVG mapping. Only names that mapping can
 * actually draw are offered: a picker that can set a symbol the tile renders as nothing is
 * worse than no picker.
 */
const PAD_SYMBOLS: readonly { name: string; title: string }[] = [
  { name: 'waveform.path', title: 'Waveform' },
  { name: 'mouth.fill', title: 'Voice' },
  { name: 'flame.fill', title: 'Heat' },
  { name: 'bolt.horizontal.fill', title: 'Hit' },
  { name: 'music.note.list', title: 'Melody' },
  { name: 'chart.bar.fill', title: 'Bass' },
  { name: 'aqi.medium', title: 'Texture' },
  { name: 'arrow.trianglehead.2.clockwise', title: 'Loop' },
  { name: 'person.3.fill', title: 'Vocals' },
  { name: 'star.fill', title: 'Favourite' },
  { name: 'circle.circle.fill', title: 'Tone' },
  { name: 'rectangle.split.3x1.fill', title: 'Slices' },
];

/** Categories the pad tile already knows how to print as its metadata kicker. */
const PAD_CATEGORIES: readonly string[] = [
  'Drums',
  'Percussion',
  'Bass',
  'Melodic',
  'Vocal',
  'FX',
  'Loop',
  'Ambience',
];

function symbolMenu(pad: Pad): void {
  presentMenu('Pad icon', [
    {
      title: 'No icon',
      detail: pad.symbolName === undefined ? 'Current' : undefined,
      onSelect: () => editPad(pad.id, 'Pad Icon', (live) => delete live.symbolName),
    },
    ...PAD_SYMBOLS.map((entry) => ({
      title: entry.title,
      symbol: entry.name,
      detail: pad.symbolName === entry.name ? 'Current' : undefined,
      onSelect: () => editPad(pad.id, 'Pad Icon', (live) => (live.symbolName = entry.name)),
    })),
  ]);
}

function categoryMenu(pad: Pad): void {
  presentMenu('Pad category', [
    {
      title: 'None',
      detail: pad.category === undefined ? 'Current' : undefined,
      onSelect: () => editPad(pad.id, 'Pad Category', (live) => delete live.category),
    },
    ...PAD_CATEGORIES.map((name) => ({
      title: name,
      detail: pad.category === name ? 'Current' : undefined,
      onSelect: () => editPad(pad.id, 'Pad Category', (live) => (live.category = name)),
    })),
    {
      title: 'Something else…',
      onSelect: () =>
        presentPrompt({
          title: 'Category',
          value: pad.category ?? '',
          onConfirm: (text) =>
            editPad(pad.id, 'Pad Category', (live) => {
              const trimmed = text.trim();
              if (trimmed === '') delete live.category;
              else live.category = trimmed;
            }),
        }),
    },
  ]);
}

function artworkMenu(pad: Pad): void {
  presentMenu('Pad artwork', [
    {
      title: pad.artworkFileName === undefined ? 'Choose a picture…' : 'Replace the picture…',
      symbol: 'square.and.arrow.up',
      // This used to build its own detached input and click it. Nothing held a reference once
      // the handler returned and the element was never in the document, so on iOS the picture
      // could be chosen and the change event never arrived: the picture simply did not appear,
      // with no error to go on.
      onSelect: () => {
        void (async () => {
          const file = await pickFile(IMAGE_ACCEPT);
          if (file === null) return;
          try {
            await setPadArtwork(pad.id, file);
          } catch (error) {
            reportFailure('That picture could not be used', error);
          }
        })();
      },
    },
    {
      title: 'Remove the picture',
      symbol: 'trash.fill',
      destructive: true,
      disabled: pad.artworkFileName === undefined,
      onSelect: () => void clearPadArtwork(pad.id),
    },
  ]);
}

function colourMenu(pad: Pad): void {
  presentMenu('Pad colour', PAD_COLORS.map((colour) => ({
    title: padColorDisplayName(colour),
    detail: pad.color === colour ? 'Current' : undefined,
    onSelect: () => editPad(pad.id, 'Pad Colour', (live) => (live.color = colour)),
  })));
}

function modeMenu(pad: Pad): void {
  presentMenu('Playback mode', PLAYBACK_MODES.map((mode) => ({
    title: playbackModeDisplayName(mode),
    detail: pad.playbackMode === mode ? 'Current' : playbackModeSummary(mode),
    onSelect: () => editPad(pad.id, 'Playback Mode', (live) => (live.playbackMode = mode)),
  })));
}

function retriggerMenu(pad: Pad): void {
  presentMenu('When it is hit again', RETRIGGER_BEHAVIOURS.map((behaviour) => ({
    title: retriggerDisplayName(behaviour),
    detail: pad.retrigger === behaviour ? 'Current' : retriggerSummary(behaviour),
    onSelect: () => editPad(pad.id, 'Retrigger', (live) => (live.retrigger = behaviour)),
  })));
}

/**
 * Quantise, said out loud.
 *
 * The setting is inert with the transport stopped: `Transport.nextQuantizedTime` returns null
 * when it is not playing, so `triggerPad` plays the pad at once and the queued state never
 * appears. Measured on a pad set to 1 Bar: stopped, it goes loaded to playing in 152 ms with
 * no `queued` in between; running, the same tap shows `queued` first. The behaviour is right
 * and it is exactly why the control looked dead, so the condition is now said in prose under
 * the title. It was in the title before, set as a kicker, which is a label: nobody read it.
 * Each row shows the division in the unit a player feels, and the current setting is named
 * rather than outlined.
 */
function quantiseMenu(pad: Pad): void {
  presentMenu(
    'Quantise',
    QUANTIZE_OPTIONS.map((option) => ({
      title: quantizeDisplayName(option),
      detail: pad.quantize === option ? 'Current' : quantiseGridSummary(option),
      onSelect: () => editPad(pad.id, 'Quantise', (live) => (live.quantize = option)),
    })),
    'Waits for the next grid line. Transport must run.',
  );
}

/** Short by measurement: the detail column is 106 px on a 320 px screen. */
function quantiseGridSummary(option: Quantize): string {
  switch (option) {
    case 'off':
      return 'No wait';
    case 'bar':
      return '4 beats';
    case 'half':
      return '2 beats';
    case 'quarter':
      return '1 beat';
    case 'eighth':
      return '1/2 beat';
    case 'sixteenth':
      return '1/4 beat';
    case 'triplet8':
      return '1/3 beat';
    case 'triplet16':
      return '1/6 beat';
  }
}

function chokeMenu(pad: Pad): void {
  const groups = [undefined, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  presentMenu('Choke group', groups.map((group) => ({
    title: group === undefined ? 'None' : `Group ${group}`,
    detail: pad.chokeGroup === group ? 'Current' : undefined,
    onSelect: () =>
      editPad(pad.id, 'Choke Group', (live) => {
        if (group === undefined) delete live.chokeGroup;
        else live.chokeGroup = group;
      }),
  })));
}

/**
 * How long a pad sounds when it was activated rather than held.
 *
 * VoiceOver's double tap, Full Keyboard Access and switch control all synthesise a click:
 * down and up arrive in the same task, so a `hold` or `gate` pad — which stops on release —
 * was torn down about ten milliseconds after it started and produced a click instead of a
 * note. There is no finger to wait for, so the release is scheduled at a musical length
 * instead. The native board takes the same 450 ms for the same reason.
 */
const SYNTHETIC_NOTE_MS = 450;

export function boardScreen(): HTMLElement {
  if (project.peek() === null) return requiresProject();

  const grid = h('div', { class: 'pad-grid instrument', role: 'group', 'aria-label': 'Pad grid' });
  const tiles = new Map<string, PadTileHandle>();
  let dragFrom: string | null = null;
  const scheduledReleases = new Set<ReturnType<typeof setTimeout>>();

  const releaseAfterNote = (target: Pad): void => {
    const timer = setTimeout(() => {
      scheduledReleases.delete(timer);
      releasePad(target);
    }, SYNTHETIC_NOTE_MS);
    scheduledReleases.add(timer);
  };

  const bank = currentBank;
  const gridSize = computed(() => bank.value?.grid ?? 'g3x4');
  /** The shape actually rendered — for `adaptive`, the one that suits this viewport. */
  const gridShape = computed(() => adaptiveGrid(gridSize.value, layout.value));
  const compact = computed(() => gridShape.value.rows >= 6 || gridShape.value.columns >= 4);

  const rebuild = (): void => {
    const current = bank.peek();
    const open = project.peek();
    if (current === null || open === null) return;
    const capacity = gridColumns(current.grid) * gridRows(current.grid);
    const wanted = [...current.pads].sort((a, b) => a.slot - b.slot).slice(0, capacity);

    // Capacity comes from the model — it decides how many pads the bank holds — while the
    // arrangement comes from the viewport. For every fixed size the two agree; for
    // `adaptive` the same twenty-four pads are 4 × 6 held upright and 6 × 4 turned sideways.
    const shape = adaptiveGrid(current.grid, layout.peek());
    grid.style.setProperty('--grid-columns', String(shape.columns));
    grid.style.setProperty('--grid-rows', String(shape.rows));

    const seen = new Set<string>();
    grid.replaceChildren();
    for (let index = 0; index < wanted.length; index++) {
      const pad = wanted[index]!;
      seen.add(pad.id);
      let handle = tiles.get(pad.id);
      if (handle === undefined) {
        handle = padTile({
          pad,
          position: { index, total: wanted.length },
          compact: compact.peek(),
          showsMeter: true,
          artworkURL: padArtworkURL,
          callbacks: {
            onPadDown: (target, context) => {
              // Nothing may run before this line.
              if (boardEditMode.peek()) return;
              // `timeStamp` shares an origin with `performance.now()`, so handing it to the
              // engine is what lets the latency figure in Settings mean touch-to-sound
              // rather than the duration of a function call.
              // Velocity as well as time: the tile has already normalised a sensorless 0.5
              // to a full hit, so what arrives here is either a real force curve or 1.
              const hit = { gestureTime: context.timeStamp, velocity: context.velocity };
              if (target.playbackMode === 'toggle') togglePad(target, hit);
              else triggerPad(target, hit);
            },
            onPadUp: (target, context) => {
              if (boardEditMode.peek()) {
                if (context.wasTap) padMenu(target);
                return;
              }
              // A synthesised activation has no finger behind it, so releasing here would
              // end the note in the same task it started.
              if (context.pointerType === 'synthetic') releaseAfterNote(target);
              else releasePad(target);
              if (context.wasTap && padIsEmpty(target)) padMenu(target);
            },
            onPadCancel: (target) => releasePad(target),
            onPadLongPress: (target) => padMenu(target),
          },
        });
        handle.el.dataset['padId'] = pad.id;
        handle.el.style.setProperty('--pad-tint', padColorVariable(pad.color));
        tiles.set(pad.id, handle);
      } else {
        handle.setPosition(index, wanted.length);
        handle.setPad(pad);
        handle.setCompact(compact.peek());
        handle.el.style.setProperty('--pad-tint', padColorVariable(pad.color));
      }
      handle.setSpokenNote(boardEditMode.peek() ? EDIT_MODE_NOTE : '');
      grid.appendChild(handle.el);
    }
    for (const [id, handle] of [...tiles]) {
      if (seen.has(id)) continue;
      handle.destroy();
      tiles.delete(id);
    }
  };

  const stopModel = effect(() => {
    revision.value;
    currentBankIndex.value;
    // A rotation re-lays an adaptive board out; for every other size this is a no-op that
    // costs one comparison.
    layout.value;
    rebuild();
  });

  /**
   * What is sounding, spoken.
   *
   * A pad fired by the sequencer, by a scene, or by another finger is silent to a
   * screen-reader user unless they happen to be focused on it — the per-tile state span is a
   * description, not a live region. This is the one place on the board that announces, and it
   * is rate-limited to the same 1.2 s the native build found necessary: without the limit a
   * two-hand roll produces a wall of speech that buries the next thing the user needs.
   */
  const soundingLive = h('p', { class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });
  const soundingNow = new Set<string>();
  let lastAnnouncement = 0;

  const announceStarted = (names: string[]): void => {
    if (names.length === 0) return;
    const at = performance.now();
    if (at - lastAnnouncement < 1200) return;
    lastAnnouncement = at;
    soundingLive.textContent = `Playing ${names.slice(0, 3).join(', ')}`;
  };

  // Live state runs off the engine's own tick, never off the model: a playing pad must not
  // cause a project mutation, and a mutation must not be needed to animate one.
  const releaseMeters = engine.acquireMeters();
  const stopLive = effect(() => {
    engine.meterTick.value;
    engine.activity.value;
    const open = project.peek();
    if (open === null) return;
    const started: string[] = [];
    // One index for the whole pass. This ran `flatMap` — twenty-four array allocations on a
    // 4 × 6 board — once per tile, i.e. once per tile per trigger, inside the synchronous
    // flush that follows a hit.
    const padsByID = new Map<string, Pad>();
    for (const entry of open.banks) for (const pad of entry.pads) padsByID.set(pad.id, pad);
    for (const [id, handle] of tiles) {
      const pad = padsByID.get(id);
      if (pad === undefined) continue;
      const state = padStateFor(pad, open);
      handle.setState(state);
      handle.setProgress(engine.progress(id));
      handle.setLevel(engine.padLevel(id));

      const sounding = state === 'playing' || state === 'looping';
      if (sounding && !soundingNow.has(id)) {
        soundingNow.add(id);
        started.push(pad.name === '' ? `pad ${pad.slot + 1}` : pad.name);
      } else if (!sounding) {
        soundingNow.delete(id);
      }
    }
    announceStarted(started);
  });

  // --- Gestures the tiles do not own ------------------------------------------------

  const pointers = new Map<number, { x: number; y: number }>();
  const offDown = on(grid, 'pointerdown', (event: PointerEvent) => {
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  });
  const offMove = on(grid, 'pointermove', (event: PointerEvent) => {
    const start = pointers.get(event.pointerId);
    if (start === undefined) return;
    if (pointers.size !== 2) return;
    const dx = event.clientX - start.x;
    if (Math.abs(dx) < 70 || Math.abs(event.clientY - start.y) > Math.abs(dx)) return;
    pointers.clear();
    changeBank(dx > 0 ? -1 : 1);
  });
  const clearPointer = (event: PointerEvent): void => {
    pointers.delete(event.pointerId);
  };
  const offUp = on(grid, 'pointerup', clearPointer);
  const offCancel = on(grid, 'pointercancel', clearPointer);

  function changeBank(delta: number): void {
    const open = project.peek();
    if (open === null || open.banks.length < 2) return;
    const next = (currentBankIndex.peek() + delta + open.banks.length) % open.banks.length;
    // The grid is rebuilt in place, so without this a bank change was an instantaneous
    // substitution of twenty-four tiles with nothing to say which way you had moved. The
    // class is direction-aware and removed by the animation's own end event, so a fast
    // double-swipe cannot leave the grid stuck mid-slide.
    grid.classList.remove('pad-grid--from-left', 'pad-grid--from-right');
    // Forcing layout between the two writes is what makes the animation restart when the
    // same direction is used twice in a row.
    void grid.offsetWidth;
    grid.classList.add(delta > 0 ? 'pad-grid--from-right' : 'pad-grid--from-left');
    currentBankIndex.value = next;
  }

  const offBankSettled = on(grid, 'animationend', (event: AnimationEvent) => {
    if (event.target === grid) {
      grid.classList.remove('pad-grid--from-left', 'pad-grid--from-right');
      return;
    }
    (event.target as HTMLElement | null)?.classList.remove('pad--settling');
  });

  // Drag-and-drop rearranging, active only in edit mode so a performance can never
  // accidentally move a pad mid-set.
  const offDragStart = on(grid, 'pointerdown', (event: PointerEvent) => {
    if (!boardEditMode.peek()) return;
    const tile = (event.target as HTMLElement).closest('[data-pad-id]') as HTMLElement | null;
    if (tile === null) return;
    dragFrom = tile.dataset['padId'] ?? null;
    tile.classList.add('pad--dragging');
  });
  // Which slot the finger is over. Without it the only feedback a drag had was the source
  // tile dimming: nothing followed the finger, no target was marked, and the swap itself
  // happened instantly, so a rearrangement read as two pads changing by magic.
  const offDragOver = on(grid, 'pointermove', (event: PointerEvent) => {
    if (dragFrom === null) return;
    const under = document.elementFromPoint(event.clientX, event.clientY);
    const target = (under as HTMLElement | null)?.closest('[data-pad-id]') as HTMLElement | null;
    const overID = target?.dataset['padId'] ?? null;
    for (const handle of tiles.values()) {
      handle.el.classList.toggle('pad--drop-target', overID !== null && overID !== dragFrom && handle.padId === overID);
    }
  });

  const offDragEnd = on(grid, 'pointerup', (event: PointerEvent) => {
    if (dragFrom === null) return;
    const from = dragFrom;
    dragFrom = null;
    for (const handle of tiles.values()) {
      handle.el.classList.remove('pad--dragging', 'pad--drop-target');
      // Both tiles acknowledge the swap. The class is cleared by `animationend` above.
      if (handle.padId === from) handle.el.classList.add('pad--settling');
    }
    const under = document.elementFromPoint(event.clientX, event.clientY);
    const target = (under as HTMLElement | null)?.closest('[data-pad-id]') as HTMLElement | null;
    const to = target?.dataset['padId'];
    if (to === undefined || to === from) return;
    mutate('Rearrange Pads', (open) => {
      const pads = open.banks[currentBankIndex.peek()]?.pads;
      if (pads === undefined) return;
      const a = pads.find((entry) => entry.id === from);
      const b = pads.find((entry) => entry.id === to);
      if (a === undefined || b === undefined) return;
      const slot = a.slot;
      a.slot = b.slot;
      b.slot = slot;
    });
  });

  const unlockSurface = lockInstrumentSurface(grid);
  const releaseWake = settings.peek().wakeLockEnabled ? holdScreenAwake('performance') : (): void => {};

  // --- Chrome -------------------------------------------------------------------------

  const master = stereoMeter({ orientation: 'horizontal', segments: 18, title: 'Master output', class: 'board-master-meter' });
  const stopMaster = effect(() => {
    engine.meterTick.value;
    const level = engine.masterLevel();
    master.setLevel({ leftRMS: level.leftRMS, rightRMS: level.rightRMS, leftPeak: level.leftPeak, rightPeak: level.rightPeak });
  });

  const gridRail = segmentedRail({
    // `adaptive` is offered now that it adapts. It used to be filtered out of the rail
    // because `gridColumns`/`gridRows` answered it with a hard-coded 3 × 4 — the option
    // existed, was listed in the brief, and would have done nothing.
    items: GRID_SIZES.map((size) => ({ id: size, title: gridDisplayName(size) })),
    selection: computed(() => gridSize.value),
    label: 'Grid size',
    onSelect: (id) =>
      mutate('Grid Size', (open) => {
        const live = open.banks[currentBankIndex.peek()];
        if (live === undefined) return;
        // `resizeBank` is the model's own growth rule: every new pad is a fresh `makePad`
        // with its own `edit` object. Spreading pad 0 instead shared its `edit` — and so its
        // `chain.nodes` array — across every pad the grid grew, and handed each of them pad
        // 0's volume, pan, mute and solo. Growing a grid while pad 0 was soloed soloed them all.
        resizeBank(live, id as (typeof GRID_SIZES)[number]);
      }),
  });

  const header = screenHeader({
    kicker: computed(() => {
      revision.value;
      return bank.value?.name ?? 'Bank';
    }),
    title: computed(() => {
      revision.value;
      return project.value?.name ?? 'Board';
    }),
    showsBack: false,
    // Four controls, not six. Undo and Redo have to be here — this is the screen whose
    // "Clear pad" dialog promises undo, and a promise the user cannot reach is not one — and
    // a 375 pt phone has room for four 44 pt targets beside a readable project name, not
    // six: with six, "Beat Kit" rendered as "Be…". Mixer, Sound Lab and Export are
    // destinations rather than actions, so they move into one menu and the two controls that
    // change this screen stay on it.
    actions: [
      ...historyButtons(),
      iconButton({
        symbol: 'square.grid.3x3.fill',
        label: 'Edit pads',
        // The mode redefines twelve controls, so the control that owns it says which way it
        // is pointing rather than leaving that to `aria-pressed` alone.
        activeLabel: 'Edit pads, on',
        active: boardEditMode,
        onPress: () => {
          boardEditMode.value = !boardEditMode.peek();
        },
      }).el,
      iconButton({
        symbol: 'ellipsis',
        label: 'More for this board',
        onPress: () =>
          presentMenu('This board', [
            {
              title: 'Mixer',
              symbol: 'slider.horizontal.3',
              detail: 'Faders, pans, mutes and the master chain',
              onSelect: () => void navigate({ name: 'mixer', projectID: project.peek()?.id }),
            },
            {
              title: 'Sound Lab',
              symbol: 'dial.medium.fill',
              detail: 'X/Y, effects and the synth',
              onSelect: () => void navigate({ name: 'sound-lab', projectID: project.peek()?.id }),
            },
            // Sending a board to another person is a different job from bouncing audio, and
            // burying it inside Export meant nobody found it: the app could import a board
            // and offered no visible way to send one.
            {
              title: 'Share this board',
              symbol: 'person.2.fill',
              detail: 'One .padlab file with every sound, that opens on their phone',
              onSelect: () => {
                const open = project.peek();
                if (open !== null) void shareProjectBackup(open.id, open.name);
              },
            },
            {
              title: 'Export audio',
              symbol: 'square.and.arrow.up',
              detail: 'Bounce the mix, the song, stems or a single sample',
              onSelect: () => void navigate({ name: 'export', projectID: project.peek()?.id }),
            },
          ]),
      }).el,
    ],
  });

  const toolbar = h(
    'div',
    { class: 'board-toolbar' },
    // Only the grid picker scrolls. It used to be the whole row, which on a 393 pt iPhone
    // pushed the master meter and the Panic button 91 % off the right edge — a stop-all
    // control you have to find by scrolling is not a stop-all control.
    h('div', { class: 'board-toolbar-scroll' }, gridRail.el),
    master.el,
    neonButton({
      title: 'Panic',
      style: 'destructive',
      onPress: () => {
        panic();
        notify('All voices stopped');
      },
    }).el,
  );

  /**
   * Edit mode silently converts every pad from "plays a sound" to "opens a menu". Nothing
   * said so: the hint was a plain paragraph toggled with `hidden`, and no pad's ARIA changed.
   * It is a live region now, and every tile carries the change in its own description, so the
   * mode is audible both when it is switched and when a pad is reached.
   */
  const editHint = h(
    'p',
    { class: 'board-hint kicker', role: 'status', 'aria-live': 'polite' },
  );
  const stopHint = effect(() => {
    const on = boardEditMode.value;
    editHint.hidden = !on;
    editHint.textContent = on ? 'Edit mode: drag to rearrange, tap a pad for its menu' : '';
    for (const handle of tiles.values()) handle.setSpokenNote(on ? EDIT_MODE_NOTE : '');
  });

  const bankStrip = h(
    'div',
    { class: 'board-banks' },
    h('button', { class: 'board-bank-step', type: 'button', 'aria-label': 'Previous bank', onclick: () => changeBank(-1) }, icon('chevron.left', { size: 16 })),
    // The row was two arrows around a name and never said what the name named. The kicker
    // stacks over it inside the column the name already occupied, so the row keeps the 57px
    // the 44pt buttons give it: pad grid 545px at 393x852 before and after, 224px landscape.
    h(
      'div',
      { class: 'board-bank-label' },
      h('span', { class: 'kicker truncate' }, 'Pad bank'),
      h('span', { class: 'board-bank-name truncate' }, computed(() => {
        revision.value;
        return bank.value?.name ?? 'Bank';
      })),
    ),
    h('button', { class: 'board-bank-step', type: 'button', 'aria-label': 'Next bank', onclick: () => changeBank(1) }, icon('chevron.right', { size: 16 })),
    // Two glyphs, not two sentences. "Delete bank" and "Add bank" spelled out took 209px of a
    // 393px row and left the bank name 40px, already truncated at "Bank A" and hopeless for
    // "Reese Bass C2 Slices". The name is the only part that says where you are, so it gets
    // the room and the actions get 44pt targets. Delete sits past Add, so the destructive one
    // is never the button next to the arrows, and it carries colour on its glyph rather than
    // on any edge.
    iconButton({
      label: 'Add bank',
      symbol: 'plus',
      onPress: () =>
        mutate('Add Bank', (open) => {
          const source = open.banks[0];
          if (source === undefined) return;
          const letter = String.fromCharCode(65 + open.banks.length);
          const copy = structuredClone(source);
          copy.id = crypto.randomUUID().toUpperCase();
          copy.name = `Bank ${letter}`;
          for (const pad of copy.pads) {
            pad.id = crypto.randomUUID().toUpperCase();
            delete pad.assetID;
            pad.name = '';
          }
          open.banks.push(copy);
        }),
    }).el,
    (() => {
      const remove = iconButton({
        label: 'Delete bank',
        symbol: 'trash.fill',
        onPress: () => confirmDeleteBank(),
      });
      remove.el.classList.add('board-bank-delete');
      // `revision` is what actually ticks on a mutate: the project signal can hand back the
      // same snapshot, which left this stuck hidden after Add bank.
      const stop = effect(() => {
        revision.value;
        remove.el.hidden = (project.value?.banks.length ?? 0) <= 1;
      });
      onCleanup(remove.el, stop);
      return remove.el;
    })(),
  );

  const screen = h(
    'div',
    { class: 'screen screen--board' },
    header,
    bankStrip,
    toolbar,
    editHint,
    grid,
    soundingLive,
  );

  const stopOrientation = effect(() => {
    screen.classList.toggle('screen--board-landscape', layout.value.orientation === 'landscape');
  });

  onCleanup(screen, () => {
    stopModel();
    stopLive();
    stopMaster();
    stopHint();
    stopOrientation();
    offDown();
    offMove();
    offUp();
    offCancel();
    offDragStart();
    offDragOver();
    offDragEnd();
    offBankSettled();
    unlockSurface();
    releaseMeters();
    releaseWake();
    master.destroy();
    gridRail.destroy();
    // A pending release outlives the screen otherwise, and fires against a torn-down engine.
    for (const timer of scheduledReleases) clearTimeout(timer);
    scheduledReleases.clear();
    for (const handle of tiles.values()) handle.destroy();
    tiles.clear();
  });

  return screen;
}
